<script lang="ts" setup>
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useLibraryLtiStore } from '@@/pinia/library_lti_store'
import { onBeforeUnmount, ref } from 'vue'

const libraryLtiStore = useLibraryLtiStore()

const xContinueButton = ref(false)
const isResuming = ref(false)

// This iframe is cross-site, so verification runs in a popup on the canonical host, where the
// session cookie works. The popup posts back when the account is linked, and we replay the
// launch. Mirrors SurfaceProtectedWhiteboardEmbed.vue's embed login.
let messageHandler: ((event: MessageEvent) => void) | null = null

function stopListening(): void {
  if (messageHandler == null) return
  window.removeEventListener('message', messageHandler)
  messageHandler = null
}

function startListening(): void {
  if (messageHandler != null) return
  const verifyOrigin = new URL(libraryLtiStore.verifyUrl, window.location.href).origin
  messageHandler = (event: MessageEvent) => {
    if (event.origin !== verifyOrigin) return
    if ((event.data ?? {}).type !== 'padlet_lti_verification_done') return
    resume()
  }
  window.addEventListener('message', messageHandler)
}

// The resume token is single-use, so a second replay would fail the launch.
function resume(): void {
  if (isResuming.value) return
  isResuming.value = true
  stopListening()
  libraryLtiStore.resumeVerifiedLaunch()
}

function verifyEmail(event: MouseEvent): void {
  if (libraryLtiStore.verifyUrl === '') return
  startListening()
  // Show the manual way through from now on. The popup reports back by itself, but nothing
  // guarantees it can: the user can close it, or a popup blocker can send them down the button's
  // new-tab fallback, which OzBaseButton marks rel="noopener". That tab can verify the email but
  // can never post back to this one.
  xContinueButton.value = true
  const popup = window.open(libraryLtiStore.verifyUrl, 'padlet_lti_verify_email', 'width=480,height=640')
  // Popup opened: cancel the anchor's own navigation so we do not open twice. If it was blocked
  // (null), let the native target="_blank" link open a tab instead.
  if (popup != null) event.preventDefault()
}

onBeforeUnmount(stopListening)
</script>

<template>
  <div class="my-5 text-dark-text-100 dark:text-light-text-100">
    <div class="px-4 py-6 text-center font-sans">
      <div class="select-none pb-2.5 w-full flex items-center justify-center">
        <OzIcon name="mail_outline" :size="64" />
      </div>
      <div class="font-semibold text-heading-3 break-words px-4 pb-2.5">
        {{ __('Verify your email to continue') }}
      </div>
      <div class="text-body-small px-8 pb-2.5">
        {{ __('To link your account, we need to verify your email address. This opens in a new window.') }}
      </div>
      <OzContainedButton
        :href="libraryLtiStore.verifyUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Primary"
        class="mx-auto"
        data-testid="libraryLtiVerifyEmailButton"
        :aria-label="__('Verify your email (opens in a new window)')"
        @click="verifyEmail"
      >
        {{ __('Verify email') }}
      </OzContainedButton>
      <div class="text-body-small px-8 pt-4">
        {{ __('This page continues once your email is verified.') }}
      </div>
      <OzContainedButton
        v-if="xContinueButton"
        :color-scheme="OzContainedButtonColorScheme.SecondaryClear"
        :disabled="isResuming"
        class="mx-auto mt-2"
        data-testid="libraryLtiVerifyEmailContinueButton"
        @click="resume"
      >
        {{ __('I’ve verified my email, continue') }}
      </OzContainedButton>
    </div>
  </div>
</template>
